import { CommonModule } from "@angular/common";
import { Component, Inject, inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { finalize } from "rxjs";
import { CourtUnavailabilityService } from "./court-unavailability.service";
import {
  CourtUnavailability,
  CourtUnavailabilityReason,
  CourtUnavailabilityRequest,
} from "./court-unavailability.types";

export interface CourtUnavailabilityDialogData {
  block?: CourtUnavailability;
  courts: Array<{ _id: string; name: string }>;
  end?: string;
  start?: string;
  venueId: string;
}

const REASONS: Array<{ label: string; value: CourtUnavailabilityReason }> = [
  { label: "Maintenance", value: "maintenance" },
  { label: "Repair", value: "repair" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Private event", value: "private_event" },
];

function localParts(value?: string) {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value ?? "");
  return { date: match?.[1] ?? "", time: match?.[2] ?? "" };
}

function addDay(date: string): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
}

export function buildCourtUnavailabilityRequest(value: {
  allDay: boolean | null;
  date: string | null;
  endTime: string | null;
  notes: string | null;
  reasonCode: CourtUnavailabilityReason | null;
  startTime: string | null;
}): CourtUnavailabilityRequest {
  const date = String(value.date);
  const crossesMidnight = String(value.endTime) < String(value.startTime);
  return {
    allDay: value.allDay === true,
    date,
    reasonCode: value.reasonCode as CourtUnavailabilityReason,
    notes: String(value.notes ?? "").trim(),
    ...(value.allDay
      ? {}
      : {
          start: `${date}T${value.startTime}:00`,
          end: `${crossesMidnight ? addDay(date) : date}T${value.endTime}:00`,
        }),
  };
}

@Component({
  selector: "app-court-unavailability-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: "./court-unavailability-dialog.component.html",
  styleUrls: ["./court-unavailability-dialog.component.scss"],
})
export class CourtUnavailabilityDialogComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(CourtUnavailabilityService);
  private readonly snackBar = inject(MatSnackBar);
  readonly reasons = REASONS;
  saving = false;
  errorMessage = "";

  private readonly startParts = localParts(
    this.data.block?.localStartDatetime ?? this.data.start,
  );
  private readonly endParts = localParts(
    this.data.block?.localEndDatetime ?? this.data.end,
  );

  readonly form = this.formBuilder.group({
    courtId: [
      this.data.block?.courtId ?? this.data.courts[0]?._id ?? "",
      Validators.required,
    ],
    date: [
      this.startParts.date || new Date().toISOString().slice(0, 10),
      Validators.required,
    ],
    allDay: [this.data.block?.allDay ?? false],
    startTime: [this.startParts.time || "09:00", Validators.required],
    endTime: [this.endParts.time || "10:00", Validators.required],
    reasonCode: [
      this.data.block?.reasonCode ??
        ("maintenance" as CourtUnavailabilityReason),
      Validators.required,
    ],
    notes: [this.data.block?.notes ?? "", Validators.maxLength(500)],
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: CourtUnavailabilityDialogData,
    private readonly dialogRef: MatDialogRef<CourtUnavailabilityDialogComponent>,
  ) {}

  save(): void {
    this.errorMessage = "";
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    if (!value.allDay && String(value.endTime) === String(value.startTime)) {
      this.errorMessage = "Start and end time cannot be the same.";
      return;
    }
    const request = buildCourtUnavailabilityRequest(value);
    const courtId = String(value.courtId);
    const operation = this.data.block
      ? this.service.update(
          this.data.venueId,
          courtId,
          this.data.block._id,
          request,
        )
      : this.service.create(this.data.venueId, courtId, request);
    this.saving = true;
    operation.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (result) => {
        this.snackBar.open("Court unavailability saved.", "Close", {
          duration: 3000,
        });
        this.dialogRef.close(result);
      },
      error: (error) => {
        this.errorMessage =
          error?.error?.message ??
          error?.error?.error?.message ??
          "Unable to block this court.";
      },
    });
  }

  cancelBlock(): void {
    const block = this.data.block;
    if (!block || this.saving) return;
    this.saving = true;
    this.service
      .cancel(this.data.venueId, block.courtId, block._id)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (result) => {
          this.snackBar.open("Court unavailability cancelled.", "Close", {
            duration: 3000,
          });
          this.dialogRef.close(result);
        },
        error: (error) => {
          this.errorMessage =
            error?.error?.message ??
            "Unable to cancel this court unavailability.";
        },
      });
  }
}
